import { computed, inject, Injectable, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';
import { PaginationStore } from '../utils/pagination-store';
import { Job, Tool, ToolJob } from '../utils/models/tool.models';
import { Toolchain, ToolchainJob } from '../utils/models/toolchain.models';
import { CapabilityService } from './api-services/capability.service';

const JOB_HISTORY_PAGE_SIZE = 8;

/** Shared, application-wide cache for capabilities and their recent jobs. */
@Injectable({ providedIn: 'root' })
export class CapabilityStore {
  private readonly capabilityService = inject(CapabilityService);
  private readonly toolHistories = new Map<number, PaginationStore<ToolJob>>();
  private readonly toolchainHistories = new Map<number, PaginationStore<ToolchainJob>>();

  readonly tools = this.capabilityService.tools;
  readonly toolchains = this.capabilityService.toolchains;
  readonly toolsById = computed(
    () => new Map(this.tools.value()?.map(tool => [tool.toolId, tool]))
  );
  readonly toolchainsById = computed(
    () => new Map(this.toolchains.value()?.map(toolchain => [toolchain.toolchainId, toolchain]))
  );
  readonly allJobHistory = new PaginationStore<Job>(JOB_HISTORY_PAGE_SIZE, pagination =>
    this.capabilityService.getJobs(pagination.page, pagination.perPage)
  );
  readonly areCapabilitiesReady = computed(() =>
    [this.tools.status(), this.toolchains.status()].every(
      status => status === 'resolved' || status === 'error'
    )
  );
  readonly areCapabilitiesReady$ = toObservable(this.areCapabilitiesReady);
  readonly areHistoriesReady = computed(() => {
    if (!this.areCapabilitiesReady()) return false;

    const tools = this.tools.hasValue() ? this.tools.value() : [];
    const toolchains = this.toolchains.hasValue() ? this.toolchains.value() : [];
    const [toolHistories, toolchainHistories] = untracked(() => [
      tools.map(tool => this.jobHistory(tool.toolId)),
      toolchains.map(toolchain => this.toolchainJobHistory(toolchain.toolchainId)),
    ]);
    return (
      !this.allJobHistory.isInitialLoad() &&
      [...toolHistories, ...toolchainHistories].every(history => !history.isInitialLoad())
    );
  });

  jobHistory(toolId: number): PaginationStore<ToolJob> {
    let history = this.toolHistories.get(toolId);
    if (!history) {
      history = new PaginationStore(JOB_HISTORY_PAGE_SIZE, pagination =>
        this.capabilityService.getToolJobs(toolId, pagination.page, pagination.perPage)
      );
      this.toolHistories.set(toolId, history);
    }
    return history;
  }

  toolchainJobHistory(toolchainId: number): PaginationStore<ToolchainJob> {
    let history = this.toolchainHistories.get(toolchainId);
    if (!history) {
      history = new PaginationStore(JOB_HISTORY_PAGE_SIZE, pagination =>
        this.capabilityService.getToolchainJobs(toolchainId, pagination.page, pagination.perPage)
      );
      this.toolchainHistories.set(toolchainId, history);
    }
    return history;
  }

  refreshToolJobHistory(toolId: number): void {
    this.allJobHistory.refresh();
    this.toolHistories.get(toolId)?.refresh();
  }

  refreshToolchainJobHistory(toolchainId: number): void {
    this.allJobHistory.refresh();
    this.toolchainHistories.get(toolchainId)?.refresh();
  }

  /** Refreshes every job history currently shown or cached in the app. */
  refreshJobHistories(): void {
    this.allJobHistory.refresh();
    for (const history of this.toolchainHistories.values()) history.refresh();
    for (const history of this.toolHistories.values()) history.refresh();
  }

  tool(toolId: number): Tool | undefined {
    return this.toolsById().get(toolId);
  }

  toolchain(toolchainId: number): Toolchain | undefined {
    return this.toolchainsById().get(toolchainId);
  }

  async deleteAllJobs(): Promise<{ success: boolean; msg: string }> {
    const result = await this.capabilityService.deleteAllJobs();
    this.refreshJobHistories();
    return result;
  }

  async waitUntilCapabilitiesReady(): Promise<void> {
    if (this.areCapabilitiesReady()) return;

    await firstValueFrom(this.areCapabilitiesReady$.pipe(filter(Boolean)));
  }
}
